import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Modal, Radio, Select, Slider, Form, Space, InputNumber, DatePicker } from 'antd';
import type { RadioChangeEvent } from 'antd';
import styles from './Dashboard.module.scss';
import DashboardImgs from '../../util/Images';
import { ageOptions } from '../../util/misc';
import { getCreditBalance } from '../../api/marketplace';
import { createPromoteData } from '../../api/marketplace';
import success from '../../static/images/dashboard/success.png'

const CheckboxGroup = Checkbox.Group;

interface PromoteProps {
  open: boolean; 
  closeModal: () => void;  
  itemDetails: any , 
  onRefreshData: () => Promise<void>;
}
interface CreditBalanceResponse {
  balance: number;
}

interface PromoteDetails {
    adGoal: string,
    ageFrom: string,
    ageTo: string,
    dailyBudget: number,
    duration: {
        from: string,
        to: string
    },
    gender: string
}
export interface PayloadDetails {
    damId: string,
    visuality: string,
    gender: string,
    ageRange: string,
    dailyBudgetCredits: number,
    startDate: string,
    endDate: string
}
function PromoteModal({open, itemDetails, closeModal, onRefreshData}: PromoteProps) {

    const [form] = Form.useForm();
    const fromValue = Form.useWatch(["duration", "from"], form);

    const [ageFrom, setAgeFrom] = useState("18");
    const [ageTo, setAgeTo] = useState("65");
    const [step, setStep] = useState(1);
    const [creditBalance, setCreditBalance] = useState<number>(0);
    const [finalFormData, setFinalFormData] = useState({} as PromoteDetails);
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [showSuccess, setShowSuccess] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const isProd = process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.REACT_APP_ENVIRONMENT === 'production'
    const billingUrl = isProd ? 'https://agentworks.synergetics.ai/billing/overview' : 'https://agentworks-stg.synergetics.ai/billing/overview'

    const nextStep = async () => {
        try {
            setErrorMessage(null)
            const values = await form.validateFields();
            setFinalFormData((prev: any) => ({
                ...prev,
                ...values,
            }));
            setStep((prev) => Math.min(prev + 1, 3));
        } catch (err) {
        }
    };

    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

    const ageToOptions = ageOptions.filter(o => Number(o.value) > Number(ageFrom));

    const ageFromChange = (value: string) => {
        setAgeFrom(value); 
        if (Number(value) >= Number(ageTo)) { 
            const fromAge = Number(value);
            const newAgeTo = fromAge + 1;
            setAgeTo(String(newAgeTo));
            form.setFieldValue("ageTo", String(newAgeTo));
        }
        form.setFieldValue("ageFrom", value);
    };

    const ageToChange = (value: string) => {
        setAgeTo(value);
        form.setFieldValue("ageTo", value);
    };

    const retriveCredit = async () => {
        try {
            const response: CreditBalanceResponse = await getCreditBalance();
            setCreditBalance(response.balance);
        } catch (error: any) {
            console.log("Failed to get credit balance", error);
        }
    };

    useEffect(() => {
        if (open) {
            form.resetFields();
            setStep(1);
            setAgeFrom("18");
            setAgeTo("65");
        }
        retriveCredit();
    }, [open]);

    const handleSubmit = async () => {
        try {
            setErrorMessage(null)
            setSubmitting(true)
            const values = await form.validateFields();
            const mergedData: PromoteDetails = {
                ...finalFormData,
                ...values,
            };

            if (mergedData.dailyBudget > creditBalance) {
                setErrorMessage('Budget should not be more than credit balance')
                return;
            }
            const fromDate = mergedData.duration.from
            const toDate = mergedData.duration.to
            const startDate = new Date(fromDate).toISOString().split("T")[0];
            const endDate = new Date(toDate).toISOString().split("T")[0];

            const payload:PayloadDetails = {
                "damId": itemDetails.id,
                "visuality": mergedData.adGoal,
                "gender": mergedData.gender,
                "ageRange": `${mergedData.ageFrom}-${mergedData.ageTo}`,
                "dailyBudgetCredits": mergedData.dailyBudget,
                "startDate": startDate,
                "endDate": endDate
            }

            const response = await createPromoteData(payload)
            setShowSuccess(true)
        } catch (error:any) {
            setErrorMessage(
                error?.message
            );
        }
        finally{
            setSubmitting(false)
        }
    }

    const fromDisabledDate = (current:any) => {
        if(!current) return false;

        const today = new Date()
        today.setHours(0,0,0,0)
        return current.toDate() < today
    }
    const toDisabledDate = (current:any) => {
        if (!current) return false;

        const from = form.getFieldValue(["duration", "from"]);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!from) {
            return current.toDate() < today;
        }
        return current.toDate() <= from.toDate();
    }

    return (
        <>
        <Modal
            centered
            open={open}
            onCancel={closeModal}
            width={1000}
            className={styles.promo_modal}
            footer={false}
        >
           <h2>Promote</h2> 
           <i>{itemDetails?.itemName}</i>

           <Form form={form} layout="vertical">

            <article className={styles.balanceInfo}>
                <div className={styles.left_bal}>
                    <small>Credit Balance</small>
                    <h3>{creditBalance ? creditBalance : 0}</h3>
                </div>
                <div>
                    To purchase more credits, please <br/>log-in to your {" "}
                            <a
                                href={billingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className={styles.agent}>Agent</span>
                                <span className={styles.works}>Works</span> 
                            </a>{" "}
                    account.
                </div>
            </article>

           {/* STEP 1 */}
           {step === 1 && (
                <section className={styles.formStep1}>
                    <h4>What do you want people to do when they see your ad?</h4>  

                    <Form.Item 
                        name="adGoal"
                        rules={[{ required: true, message: "Please select an ad goal" }]}
                    >
                        <Radio.Group
                            className={styles.promo_ad_value}
                            options={[
                                {
                                    value: "tryout",
                                    label: (
                                        <div>
                                            <b>Try the outfit on their DigitalTwin</b>
                                            <p>Best for increasing the chance of purchase</p>
                                        </div>
                                    ),
                                },
                                // {
                                //     value: "visitProfile",
                                //     label: (
                                //         <div>
                                //             <b>Visit Your Profile</b>
                                //             <p>Best for brand awareness and follows</p>
                                //         </div>
                                //     ),
                                // }
                            ]}
                        />
                    </Form.Item>
                </section>
            )}

            {/* STEP 2 */}
            {step === 2 && (
                <section className={styles.formStep2}>
                    <h4>Who should see your ad?</h4>

                    <div className={styles.form_row}>
                        <strong className={styles.label_title}>Gender</strong>
                        <Form.Item 
                            name="gender"
                            rules={[{ required: true, message: "Please select gender" }]}
                        >
                                    <Radio.Group>
                                        <Space direction="horizontal">
                                            <Radio value="male">Male</Radio>
                                            <Radio value="female">Female</Radio>
                                        </Space>
                                    </Radio.Group>
                        </Form.Item>
                    </div>

                    <div className={styles.form_row}>
                        <strong className={styles.label_title}>Age</strong>
                        <div className={styles.ageFromTo}>
<Form.Item
                            name="ageFrom"
                            initialValue="18"
                            rules={[{ required: true, message: "Select a valid age" }]}
                        >
                            <Select
                                style={{ width: 75 }}
                                onChange={ageFromChange}
                                options={ageOptions}
                            />
                        </Form.Item>

                        <span className={styles.dash_scale}>-</span>

                        <Form.Item
                            name="ageTo"
                            initialValue="65"
                            rules={[
                                { required: true, message: "Select a valid age" },
                                () => ({
                                    validator(_, value) {
                                        if (Number(value) > Number(form.getFieldValue("ageFrom"))) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject("Age To must be greater than Age From");
                                    }
                                })
                            ]}
                        >
                            <Select
                                style={{ width: 75 }}
                                onChange={ageToChange}
                                options={ageToOptions}
                            />
                        </Form.Item>
                        </div>
                        
                    </div>
                </section>
            )}

            {/* STEP 3 */}
            {step === 3 && (
                <section className={styles.formStep3}>
                    <div className={styles.thirdstep_box}>
                        <aside>
                            <div className={styles.dailyBudget_title}>Daily Budget</div>

                            

                            {/* <Form.Item 
                                name="duration"
                                initialValue={1}
                                rules={[{ required: true, message: "Select a duration" }]}
                            >
                                <label><b>1 day</b> </label>
                                <Slider max={10} />
                            </Form.Item> */}
                                    <Form.Item label="Duration" required>
                                        <Space.Compact style={{ width: "100%" }}>
                                            <Form.Item
                                                name={["duration", "from"]}
                                                noStyle
                                                rules={[{ required: true, message: "From date is required" }]}
                                            >
                                                <DatePicker placeholder="From date"
                                                disabledDate={fromDisabledDate}
                                                />
                                            </Form.Item>

                                            <span style={{ padding: "0 8px" }}>to</span>

                                            <Form.Item
                                                name={["duration", "to"]}
                                                noStyle
                                                dependencies={[["duration", "from"]]}
                                                rules={[
                                                    { required: true, message: "To date is required" },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            const from = getFieldValue(["duration", "from"]);
                                                            if (!value || !from || value.isAfter(from, "day")) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error("To date must be after From date")
                                                            );
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <DatePicker placeholder="To date"
                                                    disabledDate={toDisabledDate}
                                                />
                                            </Form.Item>
                                        </Space.Compact>
                                    </Form.Item>

                        </aside>

                        <aside>
                            <small>Ad budget</small>
                            <div className={styles.value}>
                                        <Form.Item name="dailyBudget" rules={[{ required: true, message: "Please enter budget credits" }, { type: "number", min: 1, message: "Daily budget credits must be greater than 0" },]}>
                                            <InputNumber placeholder='Enter credits' />
                                        </Form.Item>
                                </div>
                            {/* <small>Estimated Reach</small>
                            <div className={styles.value}><b>3,000 - 5,000</b></div> */}

                            <div className={styles.pay_btn_row}>
                                <Button className={styles.custom_ant_btn} onClick={closeModal}>Cancel</Button>
                                <Button className={styles.custom_ant_btn} type="primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>Submit</Button>
                            </div>
                            {errorMessage && (
                                    <div style={{ color: "#ff4d4f", marginTop: 8, fontSize: 13 }}>
                                        {errorMessage}
                                    </div>
                                )}
                        </aside>
                    </div>
                </section>
            )}

            </Form>

            {/* FOOTER BUTTONS */}
            <div className={styles.footer_btn_row}>
                {step > 1 && (
                    <Button className={styles.custom_ant_btn} onClick={prevStep}>
                       <img src={DashboardImgs.ArrowRight}/> Previous
                    </Button>
                )}

                {step < 3 && (
                    <Button className={styles.custom_ant_btn} type="primary" onClick={nextStep}>
                        Next <img src={DashboardImgs.ArrowNext}/>
                    </Button>
                )}
            </div>

            </Modal>
            <Modal
                open={showSuccess}
                footer={null}
                centered
                closable={false}
            >
                <div style={{ textAlign: "center", padding: 24 }}>
                    <img
                        src={success}
                        alt="Success"
                        style={{ width: 80, marginBottom: 16 }}
                    />

                    <p style={{ color: "#1677ff", fontWeight: 500 }}>
                        Your promotion has been activated.
                    </p>

                    <Button
                        type="primary"
                        style={{ marginTop: 16 }}
                        onClick={() => {
                            setShowSuccess(false);
                            setErrorMessage(null)
                            closeModal();
                            onRefreshData();
                        }}
                    >
                        OK
                    </Button>
                </div>
            </Modal>
        </>
    );
}

export default PromoteModal;
